import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { deleteGroup, readAccessList } from "../../../api/access/accessApi";
import type { ListedGroup } from "../../../api/access/accessModel";
import { useLoad } from "../../../shared/loading/useLoad";
import { appPaths } from "../../../shared/routing/appPaths";
import AppButton from "../../../ui/buttons/AppButton";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import ConfirmAction from "../../../ui/overlays/ConfirmAction";
import { useAccessChanges, NewGroupDialog, GroupList, groupNamer } from "../../access";

/**
 * Administration › Groups (redesign R3-3 `admin-groups`, accounts.md GS-1): the
 * installation's groups, first on top, Administrators pinned on top and Everyone last. A
 * group inside another sits right below it and moves with it.
 */
export default function InstallationGroupsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const load = useCallback((signal: AbortSignal) => readAccessList(null, signal), []);
  const { state, reload, refresh } = useLoad(load);
  const changes = useAccessChanges(refresh, state);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<ListedGroup | null>(null);

  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} />;
  const list = state.value;
  const namer = groupNamer(t, list);
  return (
    <div className="space-y-4">
      <GroupList
        list={list}
        namer={namer}
        groupPath={appPaths.administrationGroup}
        changes={changes}
        action={list.givable.size > 0 ? <AppButton variant="primary" size="sm" onClick={() => { setCreating(true); }}>{t("access.newGroupButton")}</AppButton> : undefined}
        menuItems={(group) => (group.manageable && group.system === null
          ? [{ id: "delete", label: t("access.delete"), action: { kind: "select", onSelect: () => { setDeleting(group); } } }]
          : [])}
      />
      {changes.feedback}
      {creating ? (
        <NewGroupDialog list={list} namer={namer} onClose={() => { setCreating(false); }}
          onCreated={(groupId) => { void navigate(appPaths.administrationGroup(groupId)); }} />
      ) : null}
      {deleting === null ? null : (
        <ConfirmAction title={t("access.deleteTitle", { group: namer.name(deleting.groupId) })} confirmLabel={t("access.delete")} tone="danger"
          onConfirm={async () => { await deleteGroup(deleting.groupId); refresh(); }} onClose={() => { setDeleting(null); }}>
          <p>{t("access.deleteInstallationExplained")}</p>
        </ConfirmAction>
      )}
    </div>
  );
}
